<template>
  <div>
    <div class="d-flex justify-content-between p-3">
      <div>
        <span class="pageTitle mb-2">
          {{ pageTitle }}
        </span>
      </div>
      <div class="d-flex justify-content-end">
        <div class="col-md-6">
          <div class="enableSwitch">
            <l-switch
              v-model="catalogue.isActive"
              on-text="ON"
              off-text="OFF"
            ></l-switch>
          </div>
        </div>
      </div>
    </div>
    <card>
      <div class="uploadingOverlay" v-if="uploading">
        <l-spinner
          class="upload-spinner"
          :main="true"
          :title="spinnerTip"
          size="medium"
        ></l-spinner>
      </div>

      <DeleteConfirmation
        :currentDeleteMethod="confirmDeleteVideo"
        ref="confirmCatLanguageDelete"
        @delete="$event()"
      />

      <DeleteConfirmation
        :currentDeleteMethod="confirmChildElementDelete"
        ref="ChildElementDelete"
        @delete="$event()"
      />
      <b-modal
        id="canonicalConfirmModal"
        ref="canonicalConfirm"
        title="Verify canonical URL"
        ok-title="Save Anyway"
        cancel-title="Cancel"
        ok-variant="warning"
        no-close-on-backdrop
        @ok="proceedSaveWithoutCanonical"
      >
        <p style="margin-bottom: 6px;">
          Please verify the canonical URL to save changes.
        </p>
        <p class="text-muted" style="margin-bottom: 0; font-size: 13px;">
          If you continue without verifying, the canonical URL change will be
          ignored and the existing value will be kept.
        </p>
      </b-modal>

      <b-modal
        id="webpConvertModal"
        ref="webpConvertModal"
        size="lg"
        :title="webpModalTitle"
        :hide-footer="webpStage === 'previewLoading' || webpStage === 'running'"
        :no-close-on-backdrop="true"
        :no-close-on-esc="webpStage === 'running'"
        :hide-header-close="webpStage === 'running'"
      >
        <div
          v-if="webpStage === 'previewLoading'"
          class="text-center"
          style="padding: 20px;"
        >
          <i
            class="el-icon-loading"
            style="font-size: 32px; color: #6c757d;"
          ></i>
          <p class="mt-2" style="color: #6c757d;">Scanning subtree…</p>
        </div>

        <div v-else-if="webpStage === 'error'" style="padding: 8px;">
          <p style="color: #dc3545; font-weight: 600;">
            Could not start conversion
          </p>
          <p class="text-muted" style="font-size: 13px;">
            {{ webpErrorMessage }}
          </p>
        </div>

        <div v-else-if="webpStage === 'preview' && webpPreview">
          <div class="row" style="margin-bottom: 12px;">
            <div class="col-sm-6">
              <small class="text-muted d-block">Subtree</small>
              <div>
                <strong>
                  {{
                    (webpPreview.subtree &&
                      webpPreview.subtree.catalogueCount) ||
                    0
                  }}
                </strong>
                catalogue(s) +
                <strong>
                  {{
                    (webpPreview.subtree && webpPreview.subtree.videoCount) || 0
                  }}
                </strong>
                video(s)
              </div>
            </div>
            <div class="col-sm-6">
              <small class="text-muted d-block">Images to convert</small>
              <div>
                <strong>{{ webpPreviewThumbsToConvert }}</strong>
                thumbnails +
                <strong>{{ webpPreviewCoversToConvert }}</strong>
                coverarts
              </div>
            </div>
          </div>

          <div
            v-if="webpPreviewTotal > 0"
            style="
              background: #fff3cd;
              border: 1px solid #ffeeba;
              border-radius: 4px;
              padding: 8px 12px;
              margin-bottom: 12px;
            "
          >
            <small>
              <strong>{{ webpPreviewTotal }}</strong>
              image(s) to process. Will run in
              <strong>{{ webpPreviewBatchCount }}</strong>
              batch(es) of up to 50 images each.
            </small>
          </div>
          <div
            v-else
            style="
              background: #d1ecf1;
              border: 1px solid #bee5eb;
              border-radius: 4px;
              padding: 8px 12px;
              margin-bottom: 12px;
            "
          >
            <small>
              All images in this subtree already have WebP versions. Nothing to
              convert.
            </small>
          </div>

          <div
            v-if="webpPreview.results && webpPreview.results.length"
            style="
              max-height: 280px;
              overflow: auto;
              border: 1px solid #eee;
              border-radius: 4px;
            "
          >
            <table
              style="width: 100%; font-size: 12px; border-collapse: collapse;"
            >
              <thead style="background: #f8f9fa; position: sticky; top: 0;">
                <tr>
                  <th
                    style="
                      padding: 6px 8px;
                      text-align: left;
                      border-bottom: 1px solid #ddd;
                    "
                  >
                    Type
                  </th>
                  <th
                    style="
                      padding: 6px 8px;
                      text-align: left;
                      border-bottom: 1px solid #ddd;
                    "
                  >
                    Name
                  </th>
                  <th
                    style="
                      padding: 6px 8px;
                      text-align: left;
                      border-bottom: 1px solid #ddd;
                    "
                  >
                    Language
                  </th>
                  <th
                    style="
                      padding: 6px 8px;
                      text-align: left;
                      border-bottom: 1px solid #ddd;
                    "
                  >
                    Thumbnail
                  </th>
                  <th
                    style="
                      padding: 6px 8px;
                      text-align: left;
                      border-bottom: 1px solid #ddd;
                    "
                  >
                    Coverart
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="rec in webpPreview.results">
                  <tr
                    v-for="(lng, li) in (rec.languages || [])"
                    :key="rec.id + '-prev-' + (lng.language || li)"
                    style="border-bottom: 1px solid #f0f0f0;"
                  >
                    <td style="padding: 5px 8px;">
                      <span
                        :style="{
                          color: rec.type === 'video' ? '#007bff' : '#28a745',
                          textTransform: 'capitalize',
                        }"
                      >
                        {{ rec.type }}
                      </span>
                    </td>
                    <td style="padding: 5px 8px;">{{ rec.name || rec.id }}</td>
                    <td style="padding: 5px 8px;">{{ lng.language || '—' }}</td>
                    <td style="padding: 5px 8px;">
                      <span :style="{ color: webpStatusColor(lng.thumbnail) }">
                        {{ webpStatusLabel(lng.thumbnail) }}
                      </span>
                    </td>
                    <td style="padding: 5px 8px;">
                      <span :style="{ color: webpStatusColor(lng.coverart) }">
                        {{ webpStatusLabel(lng.coverart) }}
                      </span>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>

        <div v-else-if="webpStage === 'running'">
          <div class="text-center" style="padding: 8px 0 12px;">
            <i
              class="el-icon-loading"
              style="font-size: 24px; color: #ffc107;"
            ></i>
            <p class="mt-2" style="margin-bottom: 4px;">
              Converting images — batch
              <strong>{{ webpProgressBatch }}</strong>
              <span v-if="webpProgressTotalBatches">
                of {{ webpProgressTotalBatches }}
              </span>
            </p>
            <small class="text-muted">Do not close this window.</small>
          </div>
          <div
            style="
              background: #e9ecef;
              border-radius: 3px;
              height: 8px;
              overflow: hidden;
              margin-bottom: 12px;
            "
          >
            <div
              :style="{
                width: webpProgressPct + '%',
                height: '100%',
                background: '#ffc107',
                transition: 'width 200ms ease',
              }"
            ></div>
          </div>
          <div class="row text-center">
            <div class="col-4">
              <small class="text-muted d-block">Converted</small>
              <strong style="color: #28a745; font-size: 18px;">
                {{ webpAccConverted }}
              </strong>
            </div>
            <div class="col-4">
              <small class="text-muted d-block">Skipped</small>
              <strong style="color: #6c757d; font-size: 18px;">
                {{ webpAccSkipped }}
              </strong>
            </div>
            <div class="col-4">
              <small class="text-muted d-block">Failed</small>
              <strong style="color: #dc3545; font-size: 18px;">
                {{ webpAccFailed }}
              </strong>
            </div>
          </div>
          <div class="text-center mt-3">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary"
              @click="cancelWebpAfterBatch"
              :disabled="webpCancelRequested"
            >
              {{
                webpCancelRequested
                  ? 'Stopping after current batch…'
                  : 'Stop after current batch'
              }}
            </button>
          </div>
        </div>

        <div v-else-if="webpStage === 'result'">
          <div class="row" style="margin-bottom: 12px;">
            <div class="col-3 text-center">
              <small class="text-muted d-block">Converted</small>
              <strong style="color: #28a745; font-size: 22px;">
                {{ webpAccConverted }}
              </strong>
              <small class="d-block text-muted">
                {{ (webpAcc.converted.thumbnails || 0) }} thumb ·
                {{ (webpAcc.converted.coverarts || 0) }} cover
              </small>
            </div>
            <div class="col-3 text-center">
              <small class="text-muted d-block">Skipped</small>
              <strong style="color: #6c757d; font-size: 22px;">
                {{ webpAccSkipped }}
              </strong>
            </div>
            <div class="col-3 text-center">
              <small class="text-muted d-block">Failed</small>
              <strong
                :style="{
                  color: webpAccFailed > 0 ? '#dc3545' : '#6c757d',
                  fontSize: '22px',
                }"
              >
                {{ webpAccFailed }}
              </strong>
            </div>
            <div class="col-3 text-center">
              <small class="text-muted d-block">Batches</small>
              <strong style="font-size: 22px;">{{ webpProgressBatch }}</strong>
            </div>
          </div>

          <div
            v-if="webpCancelRequested && !webpAcc.complete"
            style="
              background: #fff3cd;
              border: 1px solid #ffeeba;
              border-radius: 4px;
              padding: 8px 12px;
              margin-bottom: 12px;
            "
          >
            <small>
              Stopped at admin's request.
              <strong>{{ webpAcc.remainingAfterThisBatch || 0 }}</strong>
              record(s) remain — reopen the converter to resume.
            </small>
          </div>
          <div
            v-else-if="webpAcc.stalled"
            style="
              background: #fff3cd;
              border: 1px solid #ffeeba;
              border-radius: 4px;
              padding: 8px 12px;
              margin-bottom: 12px;
            "
          >
            <small>
              <strong>{{ webpAcc.remainingAfterThisBatch || 0 }}</strong>
              record(s) still appear in the conversion filter, but their source
              images can't be converted (placeholder/dummy images or missing
              source URLs). Replace the source images, then run conversion
              again.
            </small>
          </div>
          <div
            v-else-if="!webpAcc.complete"
            style="
              background: #fff3cd;
              border: 1px solid #ffeeba;
              border-radius: 4px;
              padding: 8px 12px;
              margin-bottom: 12px;
            "
          >
            <small>
              Hit per-call batch limit.
              <strong>{{ webpAcc.remainingAfterThisBatch || 0 }}</strong>
              record(s) remain — click "Run again" to continue.
            </small>
          </div>
          <div
            v-else
            style="
              background: #d4edda;
              border: 1px solid #c3e6cb;
              border-radius: 4px;
              padding: 8px 12px;
              margin-bottom: 12px;
            "
          >
            <small>All images processed successfully.</small>
          </div>

          <details v-if="webpAcc.results && webpAcc.results.length">
            <summary style="cursor: pointer; padding: 6px 0; font-weight: 600;">
              Show per-record details ({{ webpAcc.results.length }} records)
            </summary>
            <div
              style="
                max-height: 300px;
                overflow: auto;
                border: 1px solid #eee;
                border-radius: 4px;
                margin-top: 6px;
              "
            >
              <table
                style="width: 100%; font-size: 12px; border-collapse: collapse;"
              >
                <thead style="background: #f8f9fa; position: sticky; top: 0;">
                  <tr>
                    <th
                      style="
                        padding: 6px 8px;
                        text-align: left;
                        border-bottom: 1px solid #ddd;
                      "
                    >
                      Type
                    </th>
                    <th
                      style="
                        padding: 6px 8px;
                        text-align: left;
                        border-bottom: 1px solid #ddd;
                      "
                    >
                      Name
                    </th>
                    <th
                      style="
                        padding: 6px 8px;
                        text-align: left;
                        border-bottom: 1px solid #ddd;
                      "
                    >
                      Language
                    </th>
                    <th
                      style="
                        padding: 6px 8px;
                        text-align: left;
                        border-bottom: 1px solid #ddd;
                      "
                    >
                      Thumbnail
                    </th>
                    <th
                      style="
                        padding: 6px 8px;
                        text-align: left;
                        border-bottom: 1px solid #ddd;
                      "
                    >
                      Coverart
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="rec in webpAcc.results">
                    <tr
                      v-for="(lng, li) in (rec.languages || [])"
                      :key="rec.id + '-res-' + (lng.language || li)"
                      style="border-bottom: 1px solid #f0f0f0;"
                    >
                      <td style="padding: 5px 8px;">
                        <span
                          :style="{
                            color: rec.type === 'video' ? '#007bff' : '#28a745',
                            textTransform: 'capitalize',
                          }"
                        >
                          {{ rec.type }}
                        </span>
                      </td>
                      <td style="padding: 5px 8px;">
                        {{ rec.name || rec.id }}
                      </td>
                      <td style="padding: 5px 8px;">
                        {{ lng.language || '—' }}
                      </td>
                      <td style="padding: 5px 8px;">
                        <span
                          :style="{ color: webpStatusColor(lng.thumbnail) }"
                        >
                          {{ webpStatusLabel(lng.thumbnail) }}
                        </span>
                      </td>
                      <td style="padding: 5px 8px;">
                        <span :style="{ color: webpStatusColor(lng.coverart) }">
                          {{ webpStatusLabel(lng.coverart) }}
                        </span>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </details>
        </div>

        <template slot="modal-footer">
          <div v-if="webpStage === 'preview'">
            <button
              type="button"
              class="btn btn-light"
              @click="closeWebpModal"
              style="margin-right: 6px;"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-warning"
              :disabled="webpPreviewTotal === 0"
              @click="runWebpConversion"
            >
              Convert
            </button>
          </div>
          <div v-else-if="webpStage === 'result'">
            <button
              v-if="
                !webpAcc.complete && !webpCancelRequested && !webpAcc.stalled
              "
              type="button"
              class="btn btn-warning"
              @click="runWebpConversion"
              style="margin-right: 6px;"
            >
              Run again
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="closeWebpModal"
            >
              Close
            </button>
          </div>
          <div v-else-if="webpStage === 'error'">
            <button
              type="button"
              class="btn btn-primary"
              @click="closeWebpModal"
            >
              Close
            </button>
          </div>
        </template>
      </b-modal>
      <b-modal
        id="childdialog"
        size="lg"
        ref="associateModal"
        title="Select Child Elements"
        @ok="addChildElements"
        @shown="childDialogFlag = true"
        @hidden="closeChildElementDialog"
        ok-title="Add"
      >
        <AddChildElements
          v-if="childDialogFlag"
          ref="addchilddialog"
          :data="associateItems"
          :type="catalogue_type"
        ></AddChildElements>
      </b-modal>
      <bulk-metadata-dialog
        ref="childBulkDialog"
        record-type="catalogue"
        :child-scope="childBulkScope"
        @applied="onChildBulkApplied"
      ></bulk-metadata-dialog>
      <form @submit.prevent="saveCatalogue" class="catalogue-form" novalidate>
        <div class="row">
          <div class="col-md-12">
            <div class="row">
              <div class="col-md-6">
                <el-select
                  v-validate="'required'"
                  class="select-primary typewidth"
                  size="large"
                  placeholder="Catalogue Type"
                  v-model="catalogue_type"
                  name="catalogueType"
                  :class="{
                    'is-invalid': submitted && errors.has('catalogueType'),
                  }"
                >
                  <el-option
                    v-for="option in cataloguetypes"
                    class="select-danger"
                    :value="option.value"
                    :label="option.label"
                    :key="option.label"
                  ></el-option>
                </el-select>
                <el-select
                  :multiple="true"
                  :filterable="true"
                  :filter-method="filterMethod"
                  v-if="catalogue_type && catalogue_type != 'Root'"
                  class="select-primary typewidth second-dropdown"
                  size="large"
                  :class="{
                    'is-invalid':
                      submitted &&
                      secondCatalogue.length < 1 &&
                      catalogue_type == 'Season',
                  }"
                  v-model="secondCatalogue"
                >
                  <el-option
                    v-for="(option, index) in secondDropDown"
                    class="select-danger"
                    :value="option.value"
                    :label="option.label"
                    :key="index"
                  ></el-option>
                </el-select>
              </div>

              <div class="spacer col-md-12"></div>
              <div class="col-md-6">
                <fg-input
                  label="Name(Admin Reference)"
                  v-validate="'required'"
                  placeholder="Name"
                  name="Name"
                  v-model="catalogue.name"
                  :class="{ 'is-invalid': submitted && errors.has('Name') }"
                ></fg-input>
              </div>
              <div class="col-md-6">
                <fg-input
                  label="ID"
                  disabled=""
                  v-model="catalogue.id"
                ></fg-input>
              </div>
              <div class="col-md-12" style="margin-top: 8px;">
                <label
                  class="control-label"
                  style="display: block; font-weight: 600; margin-bottom: 4px;"
                >
                  Canonical URL
                  <small
                    class="text-muted"
                    style="font-weight: 400; margin-left: 6px;"
                  >
                    (SEO-friendly slug)
                  </small>
                </label>
                <div
                  class="d-flex align-items-start"
                  style="gap: 8px; flex-wrap: wrap;"
                >
                  <div style="flex: 1; min-width: 240px;">
                    <input
                      type="text"
                      class="form-control"
                      placeholder="e.g. action-movies"
                      v-model="canonicalUrl"
                      @input="onCanonicalUrlInput"
                    />
                    <small class="form-text" :class="canonicalUrlStatusClass">
                      {{ canonicalUrlStatusText }}
                    </small>
                    <div
                      v-if="
                        canonicalUrlSuggestions &&
                        canonicalUrlSuggestions.length
                      "
                      class="mt-1"
                    >
                      <small class="text-muted mr-1">Suggestions:</small>
                      <span
                        v-for="s in canonicalUrlSuggestions"
                        :key="s"
                        class="badge badge-pill badge-secondary mr-1"
                        style="cursor: pointer; padding: 5px 10px;"
                        @click="useCanonicalUrlSuggestion(s)"
                      >
                        {{ s }}
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    class="btn"
                    style="
                      height: fit-content;
                      align-self: flex-start;
                      background-color: #6c757d;
                      color: #ffffff;
                      border: 1px solid #6c757d;
                    "
                    :disabled="
                      !canonicalUrlDirty ||
                      canonicalUrlChecking ||
                      !canonicalUrl
                    "
                    @click="verifyCanonicalUrl"
                  >
                    {{ canonicalUrlChecking ? 'Verifying…' : 'Verify' }}
                  </button>
                  <span
                    v-if="canonicalUrlVerified"
                    style="
                      align-self: flex-start;
                      padding-top: 6px;
                      color: #28a745;
                      font-size: 22px;
                      font-weight: bold;
                      line-height: 1;
                    "
                    aria-label="Verified"
                    title="Verified"
                  >
                    ✓
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-12" v-if="!createMode" style="margin-bottom: 8px;">
            <div
              style="
                background: #f8f9fa;
                border: 1px solid #e0e0e0;
                border-radius: 6px;
                padding: 12px 16px;
              "
            >
              <div
                class="d-flex justify-content-between align-items-center"
                style="flex-wrap: wrap; gap: 8px;"
              >
                <div style="flex: 1; min-width: 280px;">
                  <div style="font-weight: 600; margin-bottom: 2px;">
                    WebP Image Conversion
                  </div>
                  <small class="text-muted">
                    Generate WebP versions of every thumbnail and coverart in
                    this catalogue and its entire subtree (child catalogues +
                    videos). Existing WebP images are skipped.
                  </small>
                </div>
                <button
                  type="button"
                  class="btn"
                  style="
                    background-color: #6c757d;
                    color: #ffffff;
                    border: 1px solid #6c757d;
                  "
                  :disabled="
                    webpStage === 'previewLoading' || webpStage === 'running'
                  "
                  @click.prevent="openWebpConverter"
                >
                  <i class="fa fa-image" style="margin-right: 4px;"></i>
                  Convert Images to WebP
                </button>
              </div>
            </div>
          </div>

          <div class="col-md-12">
            <card>
              <div
                class="d-flex justify-content-between align-items-center py-2"
              >
                <div>
                  <span class="associateTitle mb-2">
                    Associated Items
                  </span>
                </div>
                <div
                  class="d-flex justify-content-end align-items-center searchContainer"
                >
                  <div>
                    <el-input
                      type="search"
                      style="width: 200px;"
                      placeholder="Search records"
                      prefix-icon="el-icon-search"
                      v-model="searchQuery"
                      aria-controls="datatables"
                    />
                  </div>
                  <div v-if="!createMode">
                    <span class="demo-input-label ml-3 perPage">
                      Results per page:
                    </span>
                    <el-select
                      class="select-default ml-1"
                      v-model="pagination.perPage"
                      style="width: 80px;"
                      placeholder="Per page"
                    >
                      <el-option
                        class="select-default"
                        v-for="item in pagination.perPageOptions"
                        :key="item"
                        :label="item"
                        :value="item"
                      ></el-option>
                    </el-select>
                  </div>

                  <div class="mx-2 mt-1">
                    <el-button
                      type="primary"
                      icon="el-icon-plus"
                      v-if="showAddAssociatedItems"
                      @click="createAssociatedItems"
                    >
                      Add Associated Items
                    </el-button>
                    <el-dropdown
                      v-if="!createMode"
                      trigger="click"
                      @command="openChildBulkMetadata"
                    >
                      <el-button type="warning" icon="el-icon-edit-outline">
                        Bulk Metadata<i
                          class="el-icon-arrow-down el-icon--right"
                        ></i>
                      </el-button>
                      <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item command="upsert">
                          Add / Update keys on direct children
                        </el-dropdown-item>
                        <el-dropdown-item command="delete">
                          Delete keys from direct children
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </el-dropdown>
                  </div>
                </div>
              </div>
              <div>
                <div class="col-sm-12" v-if="!createMode">
                  <el-table
                    ref="childelementtable"
                    stripe
                    style="width: 100%;"
                    :data="queriedData"
                    border
                  >
                    <el-table-column label="Name">
                      <template slot-scope="props">
                        <a
                          @click="gotoVideo(props.row)"
                          class="associated-item-name"
                        >
                          {{ getChildName(props.row) }}
                        </a>
                      </template>
                    </el-table-column>
                    <el-table-column
                      prop="childType"
                      label="Type"
                    ></el-table-column>
                    <el-table-column prop="order" label="Order" width="150">
                      <template slot-scope="{ row }">
                        <fg-input
                          v-model="row.order"
                          @blur="blurChildOrderInput"
                        ></fg-input>
                      </template>
                    </el-table-column>
                    <el-table-column
                      v-if="
                        catalogue_type == 'Playlist' ||
                        catalogue_type == 'Season'
                      "
                      label="Thumbnail"
                      max-width="250"
                    >
                      <template slot-scope="props">
                        <div
                          class="thumbnailImg"
                          :style="{
                            backgroundImage:
                              'url(' + getImageUrl(props.row) + ')',
                          }"
                        ></div>
                      </template>
                    </el-table-column>
                    <el-table-column width="100" fixed="right" label="Action">
                      <template slot-scope="props">
                        <a
                          v-tooltip.top-center="'Delete'"
                          class="btn-danger btn-simple btn-link"
                          @click="deleteChildElement(props.$index, props.row)"
                        >
                          <i class="fa fa-trash"></i>
                        </a>
                      </template>
                    </el-table-column>
                  </el-table>
                </div>
              </div>
              <div
                slot="footer"
                v-if="!createMode"
                class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
              >
                <div class="">
                  <p class="card-category">
                    Showing {{ from + 1 }} to {{ to }} of {{ total }} entries
                  </p>
                </div>
                <l-pagination
                  class="pagination-no-border"
                  v-model="pagination.currentPage"
                  :per-page="pagination.perPage"
                  :total="pagination.total"
                ></l-pagination>
              </div>
            </card>
          </div>

          <div class="col-md-12">
            <card title="Catalogue Details">
              <div class="col-md-12 p-0">
                <el-button
                  type="primary"
                  icon="el-icon-plus"
                  @click="addCatalogueDetails"
                >
                  Add Catalogue Details
                </el-button>
                <!--<button class="btn btn-default" @click="addPlatform"></button>-->
              </div>
              <div class="col-md-12 col-md-12 p-0">
                <template slot="header">
                  <h4 class="card-title">Catalogue Details</h4>
                  <!-- <p class="card-category">Production Apps</p> -->
                </template>
                <el-collapse v-model="activeLanguageItems" class="mt-2">
                  <el-collapse-item
                    v-for="(item, index) in catalogue.languages"
                    :title="getTitle(item)"
                    :name="index + 1"
                    :key="index"
                  >
                    <CatLanguage
                      :ref="'catlang' + index"
                      :catalougeIndex="Number(paramID)"
                      :langaugeIndex="Number(index)"
                      :currentLanguage="item"
                      @deleteLanguage="deleteLanguage(index)"
                      :key="getKey(item)"
                      :metaData="metaData"
                      @uploadStatus="setUploadStatus"
                    ></CatLanguage>
                  </el-collapse-item>
                </el-collapse>
                <div class="col-md-12 mt-2 p-0">
                  <fg-input
                    :value="catalogue.tags.toString()"
                    @change="tagChanged"
                    label="Tags"
                    placeholder="Tags"
                    class="m-0"
                  ></fg-input>
                </div>
              </div>
            </card>
          </div>
          <div class="col-md-12 monetizationContainer">
            <card title="Monetization">
              <el-tabs>
                <el-tab-pane label="Free Preview">
                  <el-checkbox
                    @change="enableFreePreview"
                    v-model="settings.free"
                    size="medium"
                  >
                    <p>Free Preview Data</p>
                  </el-checkbox>
                </el-tab-pane>

                <el-tab-pane label="AVOD" :disabled="settings.free">
                  <el-checkbox v-model="settings.AVOD" size="medium">
                    <p>AVOD</p>
                  </el-checkbox>
                </el-tab-pane>

                <el-tab-pane label="SVOD" :disabled="settings.free">
                  <Monetization
                    ref="monetization"
                    :setting="settings"
                    createMode="createMode"
                  ></Monetization>
                </el-tab-pane>
              </el-tabs>
            </card>
          </div>

          <div class="col-md-12 col-12 manageTagContainer">
            <card title="Manage Meta Data">
              <ManageMetaData :metaData="metaData"></ManageMetaData>
            </card>
          </div>
          <div class="col-md-12 saveBtn">
            <button
              class="btn btn-primary"
              :disabled="errors.has('cattitle')"
              type="submit"
            >
              Save
            </button>
          </div>
        </div>
      </form>
    </card>
  </div>
</template>

<script>
import { FormGroupInput as FgInput } from 'src/components'
import bModal from 'bootstrap-vue/es/components/modal/modal'
import bModalDirective from 'bootstrap-vue/es/directives/modal/modal'
import {
  Select,
  Option,
  Table,
  TableColumn,
  Collapse,
  CollapseItem,
  Loading,
  Button,
  Tabs,
  TabPane,
  Checkbox,
  Dropdown,
  DropdownMenu,
  DropdownItem,
} from 'element-ui'
import {
  Switch as LSwitch,
  Checkbox as LCheckbox,
  Spinner as LSpinner,
} from 'src/components'
import { Pagination as LPagination } from 'src/components/index'
import Fuse from 'fuse.js'
import CatLanguage from '../Components/CatLanguage.vue'
import TVOD from '../Components/TVOD.vue'
import Monetization from '../Components/Monetization.vue'
import ManageMetaData from '../Components/ManageMetaData'
import BulkMetadataDialog from 'src/pages/Dashboard/Components/BulkMetadataDialog.vue'
import AddChildElements from '../Components/AddChildElements.vue'
import DeleteConfirmation from 'src/pages/Dashboard/Components/DeleteConfirmation.vue'
import * as webpHelper from '../../../service/webpHelper'
import Vue from 'vue'
export default {
  props: ['id'],
  components: {
    [Button.name]: Button,
    [Checkbox.name]: Checkbox,
    FgInput,
    bModal,
    CatLanguage,
    TVOD,
    Monetization,
    AddChildElements,
    BulkMetadataDialog,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
    bModalDirective,
    [Collapse.name]: Collapse,
    [CollapseItem.name]: CollapseItem,
    [Select.name]: Select,
    [Option.name]: Option,
    LSwitch,
    LSpinner,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    LCheckbox,
    LPagination,
    DeleteConfirmation,
    ManageMetaData,
    [Tabs.name]: Tabs,
    [TabPane.name]: TabPane,
  },
  created() {
    this.init()
  },

  mounted() {},
  computed: {
    getVideos() {
      return this.$store.state.videos.videoList
    },
    getCatalogues() {
      return this.$store.state.catalogue.catalogueList
    },
    queriedData() {
      let result = this.associateItems
      result = result.filter((item) => item.data.isActive == true)
      if (this.searchQuery !== '') {
        result = new Fuse(this.associateItems, {
          keys: [
            'data.name',
            'data.providerVideoData.title',
            'data.languages.title',
          ],
        }).search(this.searchQuery)
      }
      this.pagination.total = result.length
      return result.slice(this.from, this.to)
    },
    to() {
      let highBound = this.from + this.pagination.perPage
      if (this.total < highBound) {
        highBound = this.total
      }
      return highBound
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1)
    },
    total() {
      let result = this.associateItems
      result = result.filter((item) => item.data.isActive == true)
      this.pagination.total = result.length
      return result.length
    },
    isAdd() {
      return this.$route.path.indexOf('-1') > -1
    },
    pageTitle() {
      return this.isAdd ? ` Add Catalogue` : 'Edit Catalogue'
    },
    showAddAssociatedItems() {
      if (
        this.catalogue_type == 'Playlist' ||
        this.catalogue_type == 'Season'
      ) {
        return false
      }
      return true
    },
    // ─── Canonical URL helpers ───
    canonicalUrlDirty() {
      return (this.canonicalUrl || '') !== (this.canonicalUrlOriginal || '')
    },
    canonicalUrlStatusClass() {
      if (this.canonicalUrlStatus === 'available') return 'text-success'
      if (
        this.canonicalUrlStatus === 'unavailable' ||
        this.canonicalUrlStatus === 'invalid' ||
        this.canonicalUrlStatus === 'error'
      )
        return 'text-danger'
      return 'text-muted'
    },
    canonicalUrlStatusText() {
      if (this.canonicalUrlChecking) return 'Checking…'
      if (this.canonicalUrlStatus === 'available') return '✓ Available'
      if (this.canonicalUrlStatus === 'unavailable')
        return '✗ ' + (this.canonicalUrlMessage || 'Not available')
      if (this.canonicalUrlStatus === 'invalid')
        return '✗ ' + (this.canonicalUrlMessage || 'Invalid format')
      if (this.canonicalUrlStatus === 'error')
        return 'Could not verify. Try again.'
      if (this.canonicalUrlDirty)
        return 'Changes not verified. Click Verify before Save.'
      return 'Lowercase letters, numbers, and hyphens. 3–80 chars.'
    },
    webpModalTitle() {
      switch (this.webpStage) {
        case 'previewLoading':
          return 'WebP Conversion — Scanning…'
        case 'preview':
          return 'WebP Conversion — Preview'
        case 'running':
          return 'WebP Conversion — Running'
        case 'result':
          if (this.webpAcc.complete) return 'WebP Conversion — Complete'
          if (this.webpAcc.stalled)
            return 'WebP Conversion — Nothing more to convert'
          return 'WebP Conversion — Partial'
        case 'error':
          return 'WebP Conversion — Error'
        default:
          return 'WebP Conversion'
      }
    },
    webpPreviewThumbsToConvert() {
      if (!this.webpPreview) return 0
      var stats = this.webpPreview.stats || {}
      if (
        stats.thumbnails &&
        typeof stats.thumbnails.wouldConvert === 'number'
      ) {
        return stats.thumbnails.wouldConvert
      }
      if (stats.thumbnails && typeof stats.thumbnails.converted === 'number') {
        return stats.thumbnails.converted
      }
      if (
        this.webpPreview.converted &&
        typeof this.webpPreview.converted.thumbnails === 'number'
      ) {
        return this.webpPreview.converted.thumbnails
      }
      return (
        this.webpCountFromResults('thumbnail', 'would-convert') +
        this.webpCountFromResults('thumbnail', 'converted')
      )
    },
    webpPreviewCoversToConvert() {
      if (!this.webpPreview) return 0
      var stats = this.webpPreview.stats || {}
      if (stats.coverarts && typeof stats.coverarts.wouldConvert === 'number') {
        return stats.coverarts.wouldConvert
      }
      if (stats.coverarts && typeof stats.coverarts.converted === 'number') {
        return stats.coverarts.converted
      }
      if (
        this.webpPreview.converted &&
        typeof this.webpPreview.converted.coverarts === 'number'
      ) {
        return this.webpPreview.converted.coverarts
      }
      return (
        this.webpCountFromResults('coverart', 'would-convert') +
        this.webpCountFromResults('coverart', 'converted')
      )
    },
    webpPreviewTotal() {
      return this.webpPreviewThumbsToConvert + this.webpPreviewCoversToConvert
    },
    webpPreviewBatchCount() {
      return this.webpPreviewTotal > 0
        ? Math.ceil(this.webpPreviewTotal / 50)
        : 0
    },
    webpAccConverted() {
      return (
        (this.webpAcc.converted.thumbnails || 0) +
        (this.webpAcc.converted.coverarts || 0)
      )
    },
    webpAccSkipped() {
      return (
        (this.webpAcc.skipped.thumbnails || 0) +
        (this.webpAcc.skipped.coverarts || 0)
      )
    },
    webpAccFailed() {
      return (
        (this.webpAcc.failed.thumbnails || 0) +
        (this.webpAcc.failed.coverarts || 0)
      )
    },
    webpProgressPct() {
      var total = this.webpPreviewTotal || 0
      if (!total) return 0
      var done =
        this.webpAccConverted + this.webpAccSkipped + this.webpAccFailed
      return Math.min(100, Math.round((done / total) * 100))
    },
  },
  methods: {
    tagChanged($event) {
      this.catalogue.tags = $event.target.value.split(',')
    },
    init() {
      this.id = this.$route.params.id
      this.catalogue_type = ''
      // Vue reuses this component when only :id changes — always clear form state.
      this.metaData = []
      this.loadedMetaDataSnapshot = {}
      this.childBulkScope = null
      this.pendingMetaDataState = null
      this.associateItems = []
      this.secondIds = []
      this.secondCatalogue = []
      this.catalogue = {
        isActive: true,
        languages: [],
        tags: '',
      }
      this.canonicalUrl = ''
      this.canonicalUrlOriginal = ''
      this.canonicalUrlVerified = false
      this.canonicalUrlStatus = 'idle'
      this.canonicalUrlMessage = ''
      this.canonicalUrlSuggestions = []
      if (!this.id || this.id == -1) {
        this.createMode = true

        this.settings = {
          subscription: true,
          subscriptionIds: [],
          TVOD: false,
          AVOD: false,
          free: false,
        }
      } else {
        this.createMode = false
        this.getCatalogueData()
      }
    },
    enableFreePreview() {
      if (this.settings.free == true) {
        Vue.set(this.settings, 'TVOD', false)
        Vue.set(this.settings, 'AVOD', false)
        Vue.set(this.settings, 'subscription', false)
      }
    },
    blurChildOrderInput: function () {
      this.associateItems = this.associateItems.sort(function (a, b) {
        return a.order - b.order
      })
      this._computedWatchers.queriedData.run()
      this.$forceUpdate()
    },
    getCatalogueData: function () {
      var requestId = this.id
      this.$store.commit('setLoader', {
        show: true,
        text: 'Fetching Details',
      })
      this.$store
        .dispatch('getCategoryById', this.id)
        .then((data) => {
          // Ignore stale responses if the user already switched catalogues.
          // The loader MUST be cleared first: returning without clearing it
          // leaves the full-page spinner up forever, which looks exactly like
          // "the page keeps loading".
          if (this.id !== requestId) {
            this.$store.commit('setLoader', { show: false, text: '' })
            return
          }
          this.catalogue = data
          this.catalogue_type = this.catalogue.type
          // Existing canonical URL → pre-fill form. Empty if backend hasn't returned one.
          this.canonicalUrl = (data && data.canonicalUrl) || ''
          this.canonicalUrlOriginal = this.canonicalUrl
          this.canonicalUrlVerified = false
          this.canonicalUrlStatus = 'idle'
          this.canonicalUrlMessage = ''
          this.canonicalUrlSuggestions = []
          this.associateItems = []
          if (
            this.catalogue.childElements &&
            Array.isArray(this.catalogue.childElements)
          ) {
            this.associateItems = this.catalogue.childElements
          }
          this.metaData = []
          var metaData = this.catalogue.metaData
          for (var key in metaData) {
            var obj = {}
            obj.key = key
            obj.value = metaData[key]
            this.metaData.push(obj)
          }
          this.loadedMetaDataSnapshot = Object.assign({}, metaData || {})
          if (this.catalogue_type != 'Root') {
            this.secondIds = data['catalogueIds']
          }
          this.settings = this.catalogue.settings
          this.$store.commit('setLoader', {
            show: false,
            text: '',
          })
        })
        .catch(() => {
          // A failed or timed-out request must also clear the loader, or a
          // flaky connection leaves the page spinning with no way out.
          this.$store.commit('setLoader', { show: false, text: '' })
          this.$notify({
            title: 'Error',
            message: 'Could not load this catalogue. Please reload the page.',
            type: 'warning',
          })
        })
    },
    setSecondCatalogue: function (data) {
      this.secondDropDown = []
      if (data && Array.isArray(data)) {
        for (var i = 0; i < data.length; i++) {
          var item = {}
          item.value = data[i].id
          item.label = data[i].name
          this.secondDropDown.push(item)
          this.secondDropDownClone.push(item)
        }
      }
    },
    deleteChildElement(index, row) {
      this.selectedChildElement = row
      this.$refs['ChildElementDelete'].$refs['deleteConfirmation'].show()
    },
    confirmChildElementDelete() {
      var form = {}
      form.id = this.id
      form.data = {}
      form.data.childIds = []
      form.data.childIds.push(this.selectedChildElement.childId)
      var loadingInstance = Loading.service({ fullscreen: true })
      this.$store
        .dispatch('removeChildElements', form)
        .then((res) => {
          var fIndex = this.associateItems.findIndex(
            (a) => a.childId == this.selectedChildElement.childId,
          )
          if (fIndex >= 0) {
            this.associateItems.splice(fIndex, 1)
          }
          this.selectedChildElement = null
          loadingInstance.close()
        })
        .catch(() => {
          this.selectedChildElement = null
          loadingInstance.close()
        })
    },
    createAssociatedItems() {
      if (this.createMode) {
        this.$notify({
          title: 'Update',
          message:
            'Please create a catalogue, click edit to view and add associated catalogue',
          type: 'warning',
        })
      } else {
        this.$refs['associateModal'].show()
      }
    },
    addChildElements() {
      var newElements = this.$refs.addchilddialog.getCheckedData()
      if (newElements) {
        var form = {}
        var data = {}
        var arr = []
        for (var i = 0; i < newElements.length; i++) {
          var ele = this.associateItems.find(
            (a) => a.childId == newElements[i].childId,
          )
          if (!ele) {
            var item = {}
            item.childId = newElements[i].childId
            item.order = newElements[i].order
            arr.push(item)
          }
        }
        if (arr.length >= 1) {
          data.childElements = arr
          form.data = data
          form.id = this.id
          var loadingInstance = Loading.service({ fullscreen: true })
          this.$store
            .dispatch('addChildElements', form)
            .then((res) => {
              var data = res.data
              var catalogue = data.catalogue
              this.associateItems = catalogue.childElements
              loadingInstance.close()
            })
            .catch(() => {
              loadingInstance.close()
            })
        }
      }
    },
    buildChildBulkScope() {
      var catalogueIds = []
      var videoIds = []
      var seen = Object.create(null)
      var items = this.associateItems || []
      for (var i = 0; i < items.length; i++) {
        var row = items[i]
        if (!row || !row.childId) continue
        var id = String(row.childId)
        if (seen[id]) continue
        seen[id] = true
        if (row.childType === 'Catalogue') catalogueIds.push(id)
        else if (row.childType === 'Video') videoIds.push(id)
      }
      return {
        parentName: this.parentDisplayName(),
        catalogueIds: catalogueIds,
        videoIds: videoIds,
      }
    },

    // This page renders catalogue titles from languages[0].title elsewhere;
    // catalogue.name is not reliably populated in the edit form.
    parentDisplayName() {
      var c = this.catalogue || {}
      if (c.languages && c.languages.length && c.languages[0].title) {
        return c.languages[0].title
      }
      return c.name || 'this catalogue'
    },

    openChildBulkMetadata(command) {
      var scope = this.buildChildBulkScope()
      if (scope.catalogueIds.length + scope.videoIds.length === 0) {
        this.$notify({
          title: 'Nothing to update',
          message:
            'This catalogue has no direct children. Add associated items first.',
          type: 'warning',
        })
        return
      }
      this.childBulkScope = scope
      var self = this
      this.$nextTick(function () {
        self.$refs.childBulkDialog.open(command)
      })
    },

    onChildBulkApplied(payload) {
      this.getCatalogueData()

      var unknown =
        payload.modified === null || payload.modified === undefined
      var failedKeys = (payload.failures || [])
        .map(function (f) {
          return f.key
        })
        .filter(function (k) {
          return !!k
        })
        .join(', ')
      var message = unknown
        ? 'An unknown number of the ' +
          payload.matched +
          ' child record(s) were updated'
        : payload.modified +
          ' of ' +
          payload.matched +
          ' child record(s) updated'
      if (payload.partial) {
        message +=
          ' before the run stopped' +
          (failedKeys ? ' on: ' + failedKeys : '') +
          '. Re-run to finish, then reload to confirm.'
      } else {
        message += '.'
      }
      this.$notify({
        title: payload.partial
          ? 'Bulk update partly applied'
          : 'Bulk update finished',
        message: message,
        type: payload.partial || unknown ? 'warning' : 'success',
        duration: payload.partial ? 0 : 4500,
      })
    },

    closeChildElementDialog() {
      this.childDialogFlag = false
    },
    getChildName(row) {
      if (row.childId) {
        if (row.childType === 'Video') {
          if (row.data.languages && row.data.languages.length > 0) {
            return row.data.languages[0].title
          }
        }
        if (row.childType === 'Catalogue') {
          if (row.data.languages && row.data.languages.length > 0) {
            return row.data.languages[0].title
          }
          return 'NA'
        }
        var video = this.getVideos.find((video) => video.id == row.childId)
        if (video && video.languages && video.languages.length > 0) {
          return video.languages[0].title
        }
        var catalogue = this.getCatalogues.find(
          (catalogue) => catalogue.id == row.childId,
        )
        if (catalogue) return catalogue.name
      }
      return ''
    },
    getImageUrl(row) {
      if (row.childId) {
        var video = this.getVideos.find((video) => video.id == row.childId)
        if (video && video.languages && video.languages[0]) {
          return video.languages[0]['thumbnailLink']['source']
        }
      }
      return ''
    },
    openChildElementsDialog() {},
    getRightSecondType: function (type) {
      switch (type) {
        case 'Playlist':
          return 'Root'
        case 'Season':
          return 'Series'
        case 'Series':
          return 'Root'
        case 'Category':
          return 'Category'
      }
      return null
    },
    addCatalogueDetails: function () {
      var lang = {}
      lang.cId = this.$uuid.v4()
      lang.language = 'english'
      lang.order = 1
      lang.country = []
      lang.title = ''
      lang.description = ''
      lang.thumbnailLink = {
        small: 'https://dummyimage.com/640x360/a1a1a1/fff',
        medium: 'https://dummyimage.com/640x360/a1a1a1/fff',
        large: 'https://dummyimage.com/640x360/a1a1a1/fff',
        source: 'https://dummyimage.com/640x360/a1a1a1/fff',
      }
      lang.coverartLink = {
        small: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
        medium: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
        large: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
        source: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
      }
      lang.bannerImgLink = {
        small: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
        medium: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
        large: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
        source: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
      }
      lang.mobileBannerImgLink = {
        small: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
        medium: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
        large: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
        source: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
      }
      lang.titleImageLink = {
        small: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
        medium: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
        large: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
        source: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
      }
      lang.portraitImageLink = {
        small: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
        medium: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
        large: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
        source: 'https://dummyimage.com/1920x1080/a1a1a1/fff',
      }
      this.catalogue.languages.push(lang)
      this.activeLanguageItems.push(this.catalogue.languages.length)
    },
    getKey(item) {
      if (item._id) {
        return item._id
      }
      return item.cId
    },
    deleteLanguage: function (index) {
      this.deletedIndex = index
      this.$refs['confirmCatLanguageDelete'].$refs['deleteConfirmation'].show()
    },
    confirmDeleteVideo: function () {
      if (this.catalogue.languages) {
        this.catalogue.languages.splice(this.deletedIndex, 1)
      }
    },
    getTitle(item) {
      if (item.title) {
        return `Edit ${item.title} Details`
      }
      return 'Add Catalogue Details'
    },
    jsUcfirst(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
    // ─── Canonical URL helpers ───
    onCanonicalUrlInput() {
      // Any keystroke invalidates a prior verification — admin must re-verify.
      this.canonicalUrlVerified = false
      this.canonicalUrlStatus = 'idle'
      this.canonicalUrlMessage = ''
      this.canonicalUrlSuggestions = []
    },
    useCanonicalUrlSuggestion(s) {
      this.canonicalUrl = s
      this.canonicalUrlVerified = false
      this.canonicalUrlStatus = 'idle'
      this.canonicalUrlMessage = ''
      this.canonicalUrlSuggestions = []
    },
    verifyCanonicalUrl() {
      if (!this.canonicalUrl) return
      this.canonicalUrlChecking = true
      this.canonicalUrlStatus = 'idle'
      this.canonicalUrlMessage = ''
      this.canonicalUrlSuggestions = []
      this.$store
        .dispatch('checkCatalogueCanonicalUrl', {
          url: this.canonicalUrl,
          excludeId: this.id,
        })
        .then((result) => {
          this.canonicalUrlChecking = false
          if (result && result.available) {
            this.canonicalUrlStatus = 'available'
            this.canonicalUrlVerified = true
            // Server may normalize (trim, lowercase) — adopt the canonical form.
            if (result.normalized && result.normalized !== this.canonicalUrl) {
              this.canonicalUrl = result.normalized
            }
          } else {
            this.canonicalUrlVerified = false
            var reason = result && result.reason
            if (reason === 'format' || reason === 'reserved') {
              this.canonicalUrlStatus = 'invalid'
            } else {
              this.canonicalUrlStatus = 'unavailable'
            }
            this.canonicalUrlMessage =
              (result && result.message) || 'Not available'
            this.canonicalUrlSuggestions = (result && result.suggestions) || []
          }
        })
        .catch(() => {
          this.canonicalUrlChecking = false
          this.canonicalUrlStatus = 'error'
          this.canonicalUrlMessage = 'Could not verify'
        })
    },
    proceedSaveWithoutCanonical() {
      // "Save Anyway" → discard the unverified canonical URL change and continue saving.
      this.canonicalUrl = this.canonicalUrlOriginal
      this.canonicalUrlVerified = false
      this.canonicalUrlStatus = 'idle'
      this.canonicalUrlMessage = ''
      this.canonicalUrlSuggestions = []
      // Re-invoke save. The dirty guard now passes (value === original).
      this.saveCatalogue()
    },
    openWebpConverter() {
      var self = this
      this.webpPreview = null
      this.webpResetAccumulator()
      this.webpProgressBatch = 0
      this.webpProgressTotalBatches = 0
      this.webpCancelRequested = false
      this.webpErrorMessage = ''
      this._webpConversionQueue = null // populated by the scan
      this.webpStage = 'previewLoading'
      this.$refs.webpConvertModal.show()

      // Step 1: hard-check webp encoding support. We refuse to start on browsers
      // without canvas.toBlob('image/webp') support (Safari <16). Better than
      // running and producing zero successful conversions.
      webpHelper.isWebpEncodingSupported().then(function (supported) {
        if (!supported) {
          self.webpStage = 'error'
          self.webpErrorMessage =
            "Your browser doesn't support WebP encoding. " +
            'Use Chrome, Edge, Firefox, or Safari 16+ to run the converter.'
          return
        }
        self._scanSubtreeAndBuildPreview().catch(function (err) {
          self.webpStage = 'error'
          self.webpErrorMessage = (err && err.message) || 'Scan failed'
        })
      })
    },

    async _scanSubtreeAndBuildPreview() {
      var self = this
      var catalogues = []
      var videos = []
      var seenCat = Object.create(null)
      var seenVid = Object.create(null)
      var queue = [this.id]

      while (queue.length) {
        var nextId = queue.shift()
        if (seenCat[nextId]) continue
        seenCat[nextId] = true

        var cat = await this.$store.dispatch('getCategoryById', nextId)
        if (!cat) continue
        catalogues.push(cat)

        var children = cat.childElements || []
        for (var i = 0; i < children.length; i++) {
          var ch = children[i]
          if (!ch || !ch.childId) continue
          if (ch.childType === 'Catalogue' && !seenCat[ch.childId]) {
            queue.push(ch.childId)
          } else if (ch.childType === 'Video' && !seenVid[ch.childId]) {
            seenVid[ch.childId] = true
            if (ch.data && ch.data.languages) {
              videos.push(ch.data)
            } else {
              try {
                var v = await this.$store.dispatch('getVideoById', ch.childId)
                if (v) videos.push(v)
              } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('Video fetch failed during scan:', ch.childId, e)
              }
            }
          }
        }
      }

      var results = []
      var sampleUrl = null
      var thumbsToConvert = 0
      var coversToConvert = 0
      var workQueue = [] // each entry: { type, record, languages: [{language, links: ['thumbnail',...]}] }

      function planRecord(recordObj, kind) {
        var displayName = recordObj.name
        if (
          !displayName &&
          recordObj.languages &&
          recordObj.languages.length &&
          recordObj.languages[0].title
        ) {
          displayName = recordObj.languages[0].title
        }
        var entry = {
          type: kind,
          id: recordObj.id || recordObj._id,
          name: displayName || recordObj.id || recordObj._id,
          languages: [],
        }
        var recordWork = { type: kind, record: recordObj, languages: [] }

        ;(recordObj.languages || []).forEach(function (lang) {
          var langEntry = { language: lang.language }
          var langWork = { language: lang, slots: [] }

          ;['thumbnail', 'coverart'].forEach(function (slotKey) {
            var linkField = slotKey + 'Link'
            var linkObj = lang[linkField] || {}
            var sourceUrl = linkObj.source
            var existingWebp = linkObj.webp

            if (!webpHelper.isRealSource(sourceUrl)) {
              langEntry[slotKey] = {
                status: 'skipped',
                reason: !sourceUrl ? 'no-source-url' : 'source-is-dummy',
              }
              return
            }
            if (webpHelper.isRealSource(existingWebp)) {
              langEntry[slotKey] = {
                status: 'skipped',
                reason: 'already-has-webp',
              }
              return
            }
            // Needs conversion.
            langEntry[slotKey] = { status: 'would-convert' }
            langWork.slots.push(slotKey)
            if (slotKey === 'thumbnail') thumbsToConvert++
            else coversToConvert++
            if (!sampleUrl) sampleUrl = sourceUrl
          })

          entry.languages.push(langEntry)
          if (langWork.slots.length) recordWork.languages.push(langWork)
        })

        results.push(entry)
        if (recordWork.languages.length) workQueue.push(recordWork)
      }

      catalogues.forEach(function (c) {
        planRecord(c, 'catalogue')
      })
      videos.forEach(function (v) {
        planRecord(v, 'video')
      })

      // CORS preflight — costs one extra fetch but turns "fails after 1 image"
      // into "fails before any conversion starts".
      if (sampleUrl) {
        try {
          await webpHelper.preflightCors(sampleUrl)
        } catch (corsErr) {
          self.webpStage = 'error'
          self.webpErrorMessage = corsErr.message || 'CORS preflight failed'
          return
        }
      }

      this._webpConversionQueue = workQueue

      var totalImages = thumbsToConvert + coversToConvert
      this.webpPreview = {
        subtree: {
          catalogueCount: catalogues.length,
          videoCount: videos.length,
        },
        stats: {
          thumbnails: { wouldConvert: thumbsToConvert },
          coverarts: { wouldConvert: coversToConvert },
        },
        results: results,
      }
      this.webpProgressTotalBatches = totalImages > 0 ? 1 : 0
      this.webpStage = 'preview'
    },

    runWebpConversion() {
      this.webpResetAccumulator()
      this.webpProgressBatch = 0
      this.webpCancelRequested = false
      this.webpStage = 'running'
      this.webpProgressBatch = 1
      var self = this
      this._processNextRecord(0)
        .then(function () {})
        .catch(function (err) {
          // eslint-disable-next-line no-console
          console.error('Backfill loop crashed:', err)
          self.webpErrorMessage = (err && err.message) || 'Unexpected error'
          self.webpStage = 'result'
        })
    },

    async _processNextRecord(index) {
      var self = this
      var queue = this._webpConversionQueue || []

      if (this.webpCancelRequested || index >= queue.length) {
        this.webpAcc.complete = !this.webpCancelRequested
        this.webpAcc.remainingAfterThisBatch = Math.max(0, queue.length - index)
        this.webpStage = 'result'
        return
      }

      var item = queue[index]
      var recordResult = {
        type: item.type,
        id: item.record.id || item.record._id,
        name:
          item.record.name ||
          (item.record.languages &&
            item.record.languages[0] &&
            item.record.languages[0].title) ||
          item.record.id ||
          item.record._id,
        languages: [],
      }
      var anyConverted = false

      for (var li = 0; li < item.languages.length; li++) {
        var langWork = item.languages[li]
        var lang = langWork.language
        var langResult = { language: lang.language }

        for (var si = 0; si < langWork.slots.length; si++) {
          if (this.webpCancelRequested) break
          var slotKey = langWork.slots[si]
          var linkField = slotKey + 'Link'
          var linkObj = lang[linkField] || {}
          var sourceUrl = linkObj.source

          try {
            var bitmap = await webpHelper.fetchImageAsBitmap(sourceUrl)
            var webpBlob = await webpHelper.encodeBitmapToWebp(bitmap)

            var webpKey = webpHelper.deriveWebpKeyFromSourceUrl(sourceUrl)
            if (!webpKey)
              throw new Error('Could not derive webp S3 key from source URL')

            var preSigned = await this.$store.dispatch('preSignedRequest', {
              extension: 'webp',
              fileKey: webpKey,
              contentType: 'image/webp',
            })
            await this.$store.dispatch('uploadImageToS3', {
              url: preSigned.signedRequest,
              data: webpBlob,
            })

            lang[linkField] = Object.assign({}, linkObj, {
              webp: preSigned.url,
            })
            anyConverted = true
            langResult[slotKey] = { status: 'converted' }
            this.webpAcc.converted[slotKey + 's']++
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn(
              'Conversion failed:',
              item.type,
              recordResult.id,
              lang.language,
              slotKey,
              err,
            )
            langResult[slotKey] = {
              status: 'failed',
              error: (err && err.message) || 'unknown error',
            }
            this.webpAcc.failed[slotKey + 's']++
          }
        }
        recordResult.languages.push(langResult)
      }

      if (anyConverted && !this.webpCancelRequested) {
        try {
          var fullPayload = { languages: item.record.languages }
          if (item.type === 'catalogue') {
            await this.$store.dispatch('updateCatalogue', {
              id: item.record.id || item.record._id,
              data: fullPayload,
            })
          } else {
            await this.$store.dispatch('updateVideo', {
              id: item.record.id || item.record._id,
              data: fullPayload,
            })
          }
        } catch (patchErr) {
          // eslint-disable-next-line no-console
          console.error('PATCH failed:', item.type, recordResult.id, patchErr)
          recordResult.languages.forEach(function (lng) {
            ;['thumbnail', 'coverart'].forEach(function (sk) {
              if (lng[sk] && lng[sk].status === 'converted') {
                lng[sk].status = 'failed'
                lng[sk].error =
                  'Image uploaded but DB update failed (will retry on next run)'
                self.webpAcc.converted[sk + 's'] = Math.max(
                  0,
                  self.webpAcc.converted[sk + 's'] - 1,
                )
                self.webpAcc.failed[sk + 's']++
              }
            })
          })
        }
      }

      this.webpAcc.results.push(recordResult)

      // Yield to the event loop so the progress UI repaints. setTimeout(0)
      // would also work; we use it to give the user a visible heartbeat.
      var nextIndex = index + 1
      setTimeout(function () {
        self._processNextRecord(nextIndex)
      }, 0)
    },

    webpResetAccumulator() {
      this.webpAcc = {
        converted: { thumbnails: 0, coverarts: 0 },
        skipped: { thumbnails: 0, coverarts: 0 },
        failed: { thumbnails: 0, coverarts: 0 },
        results: [],
        complete: true,
        stalled: false,
        remainingAfterThisBatch: 0,
      }
    },

    cancelWebpAfterBatch() {
      this.webpCancelRequested = true
    },

    closeWebpModal() {
      if (this.webpStage === 'running' || this.webpStage === 'previewLoading')
        return
      this.$refs.webpConvertModal.hide()
      this.webpStage = 'idle'
      this._webpConversionQueue = null
    },

    webpStatusLabel(slot) {
      if (!slot || !slot.status) return '—'
      switch (slot.status) {
        case 'converted':
          return '✓ converted'
        case 'would-convert':
          return '→ would convert'
        case 'skipped':
          return (
            'skipped' +
            (slot.reason ? ' (' + this.webpHumanReason(slot.reason) + ')' : '')
          )
        case 'failed':
          return '✗ failed' + (slot.error ? ' — ' + slot.error : '')
        case 'not-needed':
          return '—'
        default:
          return slot.status
      }
    },

    webpStatusColor(slot) {
      if (!slot || !slot.status) return '#999'
      switch (slot.status) {
        case 'converted':
          return '#28a745'
        case 'would-convert':
          return '#17a2b8'
        case 'skipped':
          return '#6c757d'
        case 'failed':
          return '#dc3545'
        case 'not-needed':
          return '#999'
        default:
          return '#333'
      }
    },

    webpHumanReason(reason) {
      var map = {
        'already-has-webp': 'already converted',
        'no-source-url': 'no source',
        'source-is-dummy': 'placeholder image',
        'source-is-dummy-image': 'placeholder image',
        'source-fetch-failed': 'fetch failed',
      }
      return map[reason] || reason
    },

    webpCountFromResults(slotKey, statusValue) {
      if (!this.webpPreview || !Array.isArray(this.webpPreview.results))
        return 0
      var n = 0
      this.webpPreview.results.forEach(function (rec) {
        ;(rec.languages || []).forEach(function (lng) {
          if (lng[slotKey] && lng[slotKey].status === statusValue) n++
        })
      })
      return n
    },

    webpExtractError(err) {
      if (!err) return 'Unknown error'
      if (err.response && err.response.data) {
        if (err.response.data.message) return err.response.data.message
        if (typeof err.response.data === 'string') return err.response.data
      }
      if (err.message) return err.message
      return 'Network error'
    },
    saveCatalogue: function () {
      this.submitted = true
      this.$validator.validate().then((result) => {
        if (!result) {
          this.$notify({
            title: 'Error',
            message: 'Some Fields are Invalid Format!',
            type: 'warning',
          })
          return
        } else {
          // ─── Canonical URL guard: if value changed but not verified, prompt the user ───
          if (
            !this.createMode &&
            this.canonicalUrlDirty &&
            !this.canonicalUrlVerified
          ) {
            this.$refs.canonicalConfirm.show()
            return
          }
          if (this.catalogue.languages.length < 1) {
            this.$notify({
              title: 'Error',
              message: 'You need to add languages at least one!',
              type: 'warning',
            })
            return
          }
          if (
            this.catalogue_type == 'Season' &&
            this.secondCatalogue.length < 1
          ) {
            this.$notify({
              title: 'Error',
              message: 'Some Fields are Invalid Format!',
              type: 'warning',
            })
            return
          }
          var body = JSON.parse(JSON.stringify(this.catalogue))

          delete body.canonicalUrl
          var setting = this.$refs.monetization.getSettingInfo()
          if (!setting) {
            setting = JSON.parse(JSON.stringify(this.settings))
          }
          body.type = this.catalogue_type
          body.catalogueIds = []
          if (this.secondCatalogue) {
            body.catalogueIds = this.secondCatalogue
          }
          body.settings = setting
          if (this.settings.free != undefined) {
            body.settings.free = this.settings.free
          }
          if (this.settings.AVOD != undefined) {
            body.settings.AVOD = this.settings.AVOD
          }
          if (
            this.settings.subscription == false &&
            this.settings.subscriptionIds.length >= 1
          ) {
            this.settings.subscriptionIds = []
          }
          if (
            this.settings.subscription == true &&
            this.settings.subscriptionIds.length < 1
          ) {
            this.$notify({
              title: 'Error',
              message: 'You need to choose at least one SVOD!',
              type: 'warning',
            })
            return
          }
          body.childElements = this.associateItems
          body.languages = []
          body.metaData = {}
          for (var i = 0; i < this.catalogue.languages.length; i++) {
            var cItem = this.$refs['catlang' + i][0]
            if (cItem) {
              body.languages.push(cItem.getLangInfo())
            }
          }
          // Keys the user typed in the metadata table.
          var userEnteredKeys = {}
          for (var j = 0; j < this.metaData.length; j++) {
            var key = this.metaData[j].key
            if (!key) continue
            body.metaData[key] = this.metaData[j].value
            var loadedValue = (this.loadedMetaDataSnapshot || {})[key]
            var currentValue = this.metaData[j].value
            var isBlank =
              currentValue === '' ||
              currentValue === null ||
              currentValue === undefined
            if (!isBlank && currentValue !== loadedValue) {
              userEnteredKeys[key] = true
            }
          }
          for (var i = 0; i < this.catalogue.languages.length; i++) {
            var lang = this.catalogue.languages[i]
            if (!lang) continue
            if (lang.bannerImgLink && !userEnteredKeys['bannerImage']) {
              body.metaData['bannerImage'] = lang.bannerImgLink.large
            }
            if (lang.mobileBannerImgLink && !userEnteredKeys['mobileBannerImage']) {
              body.metaData['mobileBannerImage'] = lang.mobileBannerImgLink.large
            }
            if (lang.titleImageLink && !userEnteredKeys['bannerTitleImage']) {
              body.metaData['bannerTitleImage'] = lang.titleImageLink.large
            }
            if (lang.portraitImageLink && !userEnteredKeys['portraitImage']) {
              body.metaData['portraitImage'] = lang.portraitImageLink.large
            }
          }
          // CMS-3: send only what changed, and name removed keys explicitly.
          // The backend now MERGES metaData, so keys we omit are preserved
          // rather than erased. Removal must therefore be explicit or a key
          // deleted in the UI would silently come back.
          if (!this.createMode) {
            var snapshot = this.loadedMetaDataSnapshot || {}
            // The full metaData the record should have AFTER this save. Kept so
            // the snapshot can be advanced on success — without that, a second
            // save made without reloading would diff against the original load
            // and silently fail to re-add a key that an earlier save removed.
            this.pendingMetaDataState = Object.assign({}, body.metaData)
            var changed = {}
            Object.keys(body.metaData).forEach(function (k) {
              if (snapshot[k] !== body.metaData[k]) {
                changed[k] = body.metaData[k]
              }
            })
            var removed = Object.keys(snapshot).filter(function (k) {
              return !(k in body.metaData)
            })
            body.metaData = changed
            if (removed.length) {
              body.metaDataRemove = removed
            }
          }
          body.tags = this.catalogue.tags
          if (this.createMode) {
            this.$store
              .dispatch('createCatalogue', body)
              .then((data) => {
                this.$router.push({ name: 'All Catalogue' })
              })
              .catch(() => {
                this.$notify({
                  title: 'Error',
                  message: 'Create Catalogue Failed!',
                  type: 'warning',
                })
                return
              })
          } else {
            var formData = {}
            formData.data = body
            formData.id = this.id
            formData.tags = this.catalogue.tags
            this.$store
              .dispatch('updateCatalogue', formData)
              .then((data) => {
                if (this.pendingMetaDataState) {
                  this.loadedMetaDataSnapshot = this.pendingMetaDataState
                  this.pendingMetaDataState = null
                }
                //   this.$router.push({ name: "All Catalogue" });
                this.$notify({
                  title: 'Success',
                  message: 'Catalogue Updated Successfully!',
                  type: 'warning',
                })
                // Canonical URL was changed AND verified → persist via dedicated endpoint.
                if (this.canonicalUrlDirty && this.canonicalUrlVerified) {
                  this.$store
                    .dispatch('setCatalogueCanonicalUrl', {
                      id: this.id,
                      canonicalUrl: this.canonicalUrl,
                    })
                    .then((result) => {
                      var saved =
                        (result && result.canonicalUrl) || this.canonicalUrl
                      this.canonicalUrl = saved
                      this.canonicalUrlOriginal = saved
                      this.canonicalUrlVerified = false
                      this.canonicalUrlStatus = 'idle'
                      this.$notify({
                        title: 'Success',
                        message: 'Canonical URL updated.',
                        type: 'success',
                      })
                    })
                    .catch((err) => {
                      var msg = 'Could not update canonical URL.'
                      if (err && err.response && err.response.status === 409) {
                        msg =
                          'Canonical URL is already taken. Please pick another and Verify again.'
                        this.canonicalUrlStatus = 'unavailable'
                        this.canonicalUrlMessage = 'Already taken'
                        this.canonicalUrlVerified = false
                      }
                      this.$notify({
                        title: 'Error',
                        message: msg,
                        type: 'warning',
                      })
                    })
                }
              })
              .catch(() => {
                this.$notify({
                  title: 'Error',
                  message: 'Update Catalogue Failed!',
                  type: 'warning',
                })
                return
              })
          }
        }
      })
    },

    setUploadStatus: function (value) {
      this.uploading = value
    },
    filterMethod(query) {
      if (query !== '') {
        this.secondDropDown = this.secondDropDownClone.filter((item) => {
          return (
            item.label.toLowerCase().startsWith(query.toLowerCase()) === true
          )
        })
      } else {
        this.secondDropDown = this.secondDropDownClone
      }
    },
    gotoVideo(associatedItem) {
      if (associatedItem.childType == 'Video') {
        this.$router.push({
          name: 'Edit Video',
          params: { id: associatedItem.childId },
        })
      } else if (associatedItem.childType == 'Catalogue') {
        this.$router.push({
          name: 'Edit Catalogue',
          params: { id: associatedItem.childId },
        })
      }
    },
  },
  updated() {},
  watch: {
    '$route.params.id'() {
      this.init()
    },
    catalogue_type: function (cur, old) {
      if (this.catalogue_type == 'Season') {
        this.secondCatalogue = []
        var type = this.getRightSecondType(this.catalogue_type)
        if (type) {
          this.$store.commit('setLoader', {
            show: true,
            text: 'Fetching Details',
          })
          this.$store
            .dispatch('getCatalogueListByType', type)
            .then((data) => {
              this.setSecondCatalogue(data._list)
              if (!old) {
                this.secondCatalogue = this.secondIds
              }
              this.$store.commit('setLoader', {
                show: false,
                text: '',
              })
            })
            .catch(() => {})
        }
      } else {
        this.secondCatalogue = []
        var type = this.getRightSecondType(this.catalogue_type)
        if (type) {
          this.$store.commit('setLoader', {
            show: true,
            text: 'Fetching Details',
          })
          this.$store
            .dispatch('getAllCatalogue')
            .then((data) => {
              this.setSecondCatalogue(data._list)
              this.secondCatalogue = this.secondIds
              this.$store.commit('setLoader', {
                show: false,
                text: '',
              })
            })
            .catch(() => {})
        }
      }
    },
  },
  data() {
    return {
      createMode: true,
      inputPlaylistName: '',
      paramID: this.$route.params.id,
      inputValId: '',
      catalogue_type: '',
      catalogue: {
        isActive: true,
        languages: [],
        tags: '',
      },
      childDialogFlag: false,
      associateItems: [],
      searchQuery: '',
      uploading: false,
      spinnerTip: 'Uploading Images...',
      secondDropDown: [],
      secondDropDownClone: [],
      secondCatalogue: [],
      secondIds: [],
      settings: {},
      selectedChildElement: null,
      metaData: [],
      childBulkScope: null,
      loadedMetaDataSnapshot: {},
      pendingMetaDataState: null,
      currentMetaData: {
        key: '',
        value: '',
      },
      activeLanguageItems: [],
      pagination: {
        perPage: 5,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50],
        total: 0,
      },
      submitted: false,
      // ─── Canonical URL state ───
      canonicalUrl: '',
      canonicalUrlOriginal: '',
      canonicalUrlVerified: false, // true only after a successful Verify for the current value
      canonicalUrlChecking: false,
      canonicalUrlStatus: 'idle', // 'idle' | 'available' | 'unavailable' | 'invalid' | 'error'
      canonicalUrlMessage: '',
      canonicalUrlSuggestions: [],
      // ─── WebP image conversion state ───
      webpStage: 'idle', // 'idle' | 'previewLoading' | 'preview' | 'running' | 'result' | 'error'
      webpPreview: null, // dry-run response
      webpAcc: {
        // accumulator across real-run batches
        converted: { thumbnails: 0, coverarts: 0 },
        skipped: { thumbnails: 0, coverarts: 0 },
        failed: { thumbnails: 0, coverarts: 0 },
        results: [],
        complete: true,
        remainingAfterThisBatch: 0,
      },
      webpProgressBatch: 0,
      webpProgressTotalBatches: 0,
      webpCancelRequested: false,
      webpErrorMessage: '',
      cataloguetypes: [
        {
          value: 'Playlist',
          label: 'Playlist',
        },
        {
          value: 'Series',
          label: 'Series',
        },
        {
          value: 'Season',
          label: 'Season',
        },
        {
          value: 'Root',
          label: 'Root',
        },
        {
          value: 'Category',
          label: 'Category',
        },
      ],
      switches: {
        defaultOn: true,
      },
      checkedFreePreview: true,
      checkedAvod: true,
    }
  },
}
</script>

<style scoped>
.typewidth {
  width: 30%;
}

.originaldetail {
  padding-left: 15px;
  padding-top: 25px;
}

.originalimage {
  padding: 0px;
  width: 95%;
}

.originalbutton {
  padding-left: 0px;
  padding-top: 10px;
  padding-bottom: 20px;
}

.monetizationcontainer {
  padding-top: 50px;
}

.languagecontainer {
  padding-top: 30px;
}

.spacer {
  padding-top: 30px;
}

.second-dropdown {
  margin-left: 20px;
  min-width: 200px;
}

.card form.catalogue-form [class*='col-'] {
  padding-left: 15px;
  padding-right: 15px;
}

#childdialog .modal-dialog {
  width: 800px;
}

.perPage {
  font-weight: 400;
  font-size: 14px;
  color: #27282a;
  letter-spacing: 0.7px;
}

.associateTitle {
  margin-left: 15px !important;
  font-weight: 300;
  font-size: 16px;
  color: #333333;
}

.saveBtn {
  float: left;
  position: relative;
  bottom: 25px;
}

.pageTitle {
  margin-left: 15px;
  font-weight: 500;
  font-size: 22px;
  color: #27282a;
  position: relative;
  right: 30px;
}

.enableSwitch {
  float: right;
  padding-right: 20px;
  position: relative;
  left: 50px;
}

.thumbnailImg {
  height: 100px;
  width: 160px;
  background-size: contain;
  background-repeat: no-repeat;
  border-radius: 5px;
  filter: drop-shadow(0 0 1px rgba(0, 0, 0, 0.16));
}

.associated-item-name {
  cursor: pointer;
}

.associated-item-name:hover {
  color: #23ccef;
}
</style>